import DataGrid, {
    Column,
    Grouping,
    Item,
    LoadPanel,
    Paging,
    Scrolling,
    Selection,
    Toolbar,
} from 'devextreme-react/data-grid';
import DateBox from 'devextreme-react/date-box';
import { Cancelable, EventInfo } from 'devextreme/events';
import dxDataGrid, {
    DataRowTemplateData,
    InitializedEvent,
    RowPreparedEvent,
    SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import React, { useCallback, useEffect } from 'react';

import { StepDto } from '../../../../../dtos/StepDto';

import GrantDetailCommentTooltip from '../../../../grantDetails/GrantDetailCommentTooltip';
import StatusIcon from '../../../StatusIcon';
import TaskStatusIcon from '../../tasks/TaskStatusIcon';

import DataSource from 'devextreme/data/data_source';
import dxTooltip from 'devextreme/ui/tooltip';
import { GrantDto } from '../../../../../dtos/GrantDto';
import { PhaseDto } from '../../../../../dtos/PhaseDto';
import { isUploadOnlyPhase } from '../../../Utils';
import { TasksFeatureFlags, phaseNameShownInPhaseHeader } from '../../../Types';
import { TasklistMode, useTaskListSelectionState } from '../../context/TaskListContext';
import './SubTaskListView.css';
type GridRef = { instance: dxDataGrid } | null;

// Width of the group expand column: the chevron and nothing else.
const GROUP_EXPAND_WIDTH = 20;
// The status column doubles as the subtask indent; the dot itself is pushed under the task name
// by padding (see .step-row-dots in the CSS), since DevExtreme does not take a changed width.
const STATUS_COLUMN_WIDTH = 29;

export type ISubTaskListViewProps = {
    gridRef: React.MutableRefObject<dxDataGrid<StepDto, string> | null>;
    phase: PhaseDto | null;
    // Every cycle's subtasks, and the newest cycle's share of them.
    visibleSteps?: StepDto[] | null;
    currentCycleSteps?: StepDto[] | null;
    hasLoaded: boolean;
    dataSource: DataSource<StepDto, string>;
    taskListVisible: () => boolean;
    showRequestResponseSummaryButton: () => boolean;
    addButtonVisible: boolean;
    deleteButtonVisible: () => boolean;
    taskListClassName: string;
    stepCount: number;
    addButtonOptions: {
        icon: string;
        hint: string;
        text: string;
        onClick: () => void;
    };
    deleteButtonOptions: {
        icon: string;
        hint: string;
        text: string;
        type: string;
        onClick: () => void;
    };
    onSelectionChange: (e: SelectionChangedEvent<StepDto, string>) => void;
    rowPrepared: (e: RowPreparedEvent<StepDto, string>) => void;
    onContentReady: (e: EventInfo<dxDataGrid<StepDto, string>>) => void | Promise<void>;
    onTooltipHiding: (e: Cancelable & EventInfo<dxTooltip>) => void | Promise<void>;
    onClickShowRequestResponseSummary: () => void;
    dataRowTemplate: (rowInfo: DataRowTemplateData<StepDto, string>) => {
        status: { text: string; color: string; border: string };
        name: string;
        dueText: string;
        fileCount?: number;
        commentRow:
        | null
        | {
            id: string;
            snippet: string | null | undefined;
            tooltip: {
                text?: string | null;
                user?: string | null;
                date?: string | null;
            };
        };
    };
    hideStatusIcons: () => boolean;
    hideStepStatus: () => boolean;
    mode?: TasklistMode;
    flags?: TasksFeatureFlags;
    editableDates?: boolean;
    onDateChange?: (stepId: string, field: 'startDate' | 'projectedEndDate', value: Date | null) => void;
    grantData?: GrantDto | null;
    nameShownExternally?: boolean;
    collapsed: boolean;
    toggleCollapsed: () => void;
    // Cycle grouping: on once the task has more than one cycle. The label names a cycle by
    // number and the number is read off a row, so the grid can group and order by it.
    groupedByCycle: boolean;
    cycleGroupLabel: (cycle: number) => string;
    cycleNumberOf: (step: StepDto) => number;
    // Download All as a toolbar menu entry (template setting).
    downloadAllVisible: boolean;
    downloadAllOptions: {
        icon: string;
        hint: string;
        text: string;
        onClick: () => void;
    };
    // Start Resubmission as a toolbar menu entry, and what to do once one is started.
    repeatButtonVisible: boolean;
    repeatButtonOptions: {
        icon: string;
        hint: string;
        text: string;
        disabled: boolean;
        onClick: () => void;
    };
    onCycleStarted: () => void | Promise<void>;
};

function SubTaskListView(props: ISubTaskListViewProps) {
    const {
        gridRef,
        phase,
        visibleSteps,
        currentCycleSteps,
        hasLoaded,
        dataSource,
        taskListVisible,
        addButtonVisible,
        deleteButtonVisible,
        taskListClassName,
        stepCount,
        addButtonOptions,
        deleteButtonOptions,
        onSelectionChange,
        rowPrepared,
        onContentReady,
        onClickShowRequestResponseSummary,
        dataRowTemplate,
        hideStatusIcons,
        hideStepStatus,
        mode,
        editableDates = false,
        onDateChange,
        grantData,
        nameShownExternally,
        collapsed,
        toggleCollapsed,
        groupedByCycle,
        cycleGroupLabel,
        cycleNumberOf,
        downloadAllVisible,
        downloadAllOptions,
        repeatButtonVisible,
        repeatButtonOptions,
    } = props;

    const { selectedStep } = useTaskListSelectionState();

    const isPhaseTracked = phase?.defaultPhase?.settings?.tracked ?? false;
    const uploadOnly = isUploadOnlyPhase(phase);
    // The status column stays as an indent when the rows show no status, unless the grid is
    // grouped by cycle - the group expander column indents those rows already.
    const statusColumnShown = !hideStepStatus() || !groupedByCycle;

    // Phases whose name lives in an always-visible phase header leave the toolbar as an empty grey
    // bar - hide it unless it still holds a menu entry. Either the task has the extended header, or
    // the caller says so via nameShownExternally.
    // Note this stays visible until the phase loads (it arrives async): the toolbar must default to
    // shown, or every other task type mounts with a hidden toolbar and never gets it back.
    const nameInPhaseHeader = phaseNameShownInPhaseHeader(phase, mode === "templates")
        || (!!nameShownExternally && mode !== "templates");
    const showTaskName = phase !== null && !nameInPhaseHeader;
    // Download All lives in this toolbar only where the toolbar is the task header - the extended
    // header carries its own icon.
    const downloadAllInMenu = downloadAllVisible && showTaskName;
    const showToolbar = !nameInPhaseHeader || addButtonVisible || deleteButtonVisible();

    // Where the toolbar is acting as the task header, the panel's rule reads as a divider between
    // tasks rather than an underline for this one. Tasks with the extended header keep it - there
    // the rule sits under that row, where it belongs.
    const toolbarIsTaskHeader = showTaskName;

    // The expander only renders where this toolbar is the task header, so honour a collapse only
    // there. Without this a finished DC task - whose name lives in its own phase header - would
    // auto-collapse with no control anywhere to reopen it.
    const rowsHidden = collapsed && showTaskName;
    // The repeat entry lives in this toolbar only where the toolbar is the task header - DC hosts
    // the action in its phase header instead.
    const repeatInMenu = repeatButtonVisible && showTaskName;

    const dataRowRender = (rowInfo: DataRowTemplateData<StepDto, string>) => {
        const row = dataRowTemplate(rowInfo);
        const stepData = rowInfo.data;
        const columnCount = (statusColumnShown ? 3 : 2) + (groupedByCycle ? 1 : 0);

        return (
            <>
                <tr role="row" className={hideStepStatus() ? "step-row" : "step-row step-row-dots"}>
                    {/* Fills the group expand column so the cells after it line up with the
                        grid's own columns. */}
                    {groupedByCycle && <td role="gridcell" className="step-group-space-cell" />}
                    {statusColumnShown && (
                        <td role="gridcell" className="step-status-cell">
                            {/* Smaller than the task's own 22px icon and set under the task name,
                                so a subtask reads as sitting under its task (ticket 1034). Rows
                                with no status keep an empty cell so their names sit under the
                                task name instead. */}
                            {!hideStepStatus() && <StatusIcon info={row.status} diameter={14} />}
                        </td>
                    )}
                    <td role="gridcell" className="step-name-cell">
                        {row.name}
                        {/* File count is a per-instance signal; hide it in the templates admin. */}
                        {uploadOnly && mode !== "templates" && (
                            <span style={{ marginLeft: 6, color: "#666", fontSize: 12 }}>({row.fileCount ?? 0})</span>
                        )}
                    </td>
                    <td role="gridcell">
                        <span>{row.dueText}</span>
                    </td>
                </tr>

                {row.commentRow && (
                    <tr role="row" className="comments-row">
                        <td id={row.commentRow.id} colSpan={columnCount} style={{ paddingTop: 0, paddingBottom: 0 }}>
                            <span>{row.commentRow.snippet}</span>
                            <GrantDetailCommentTooltip
                                commentText={row.commentRow.tooltip.text as string}
                                commentUser={row.commentRow.tooltip.user as string}
                                commentDate={row.commentRow.tooltip.date as string}
                                target={row.commentRow.id}
                            />
                        </td>
                    </tr>
                )}
            </>
        );
    };

    const initializedGrid = useCallback((e: InitializedEvent) => {
        // store the **dxDataGrid** instance
        (gridRef as React.MutableRefObject<dxDataGrid<StepDto, string> | null>).current =
            e.component as dxDataGrid<StepDto, string>;

        e.component?.columnOption('command:edit', 'width', 0);
    }, [gridRef]);

    // Keep grid selection in sync when selectedStep changes (e.g., after saving a new subtask).
    // Only the task that actually lists the subtask selects it: a grid asked for a key it does
    // not hold goes back to the store for it, so telling every task's grid to select the step
    // started a load in each of the others and left them handing the selection back and forth
    // until the page settled on nothing (ticket 1066). visibleSteps is this task's own loaded,
    // permission-filtered list, so a subtask saved into this task selects as soon as it lands.
    useEffect(() => {
        const stepId = selectedStep?.id;
        if (!stepId) return;
        if (!visibleSteps?.some(s => s.id === stepId)) return;

        // Use setTimeout to ensure the grid has finished any pending updates
        setTimeout(() => {
            const grid = gridRef?.current;
            if (!grid) return;
            if (grid.getSelectedRowKeys().includes(stepId)) return;
            grid.selectRows([stepId], false);
        }, 50);
    }, [selectedStep?.id, visibleSteps, gridRef]);

    // Hide the entire task card when user has no permission to view it
    if (hasLoaded && !taskListVisible()) {
        return null;
    }

    // A collapsed task drops the selected shading, the way the extended header drops it when its
    // panel closes - the selection itself is untouched, so it comes back on expanding. Keyed on
    // rowsHidden rather than collapsed: a completed task starts collapsed whether or not the
    // toolbar is its header, and with the extended header the shading belongs to the header row.
    const selectionClassName = rowsHidden ? 'task-list-phase' : taskListClassName;

    return (
        <div
            className={`tasklist-task ${selectionClassName}${showToolbar ? '' : ' tasklist-no-toolbar'}${rowsHidden ? ' tasklist-collapsed' : ''}${toolbarIsTaskHeader ? ' tasklist-header-toolbar' : ''}`}
            data-testid={rowsHidden ? 'tasklist-collapsed' : 'tasklist-expanded'}
            style={{ width: '100%', padding: '5px' }}
        >
            <DataGrid<StepDto, string>
                dataSource={dataSource}
                onSelectionChanged={onSelectionChange}
                showColumnHeaders={false}
                repaintChangesOnly={true}
                showColumnLines={false}
                onInitialized={initializedGrid}
                onRowPrepared={rowPrepared}
                onContentReady={onContentReady}
                dataRowRender={dataRowRender}
                visible={taskListVisible()}
            >
                <LoadPanel enabled={false} />

                {/* Grouping brings an expand command column the grid will not drop (visible=false
                    is ignored). Declared first so it is the first column, kept narrow, and the
                    custom row template pads each row with a matching cell - without that the
                    subtask names sat in this column and the group label in the name column.
                    rowClick so the whole group row toggles. */}
                <Column type="groupExpand" width={GROUP_EXPAND_WIDTH} />

                {statusColumnShown && (
                    <Column dataField="status" dataType="number" width={STATUS_COLUMN_WIDTH} />
                )}
                <Column dataField="name" dataType="string" width="100%" />
                <Column dataField="dueDate" dataType="date" width={120} visible={false} />
                {/* Cycle group: every cycle's subtasks are on the grid, newest cycle first, each
                    under a header naming it (ticket 1069). Kept mounted with the group index
                    toggled rather than rendered conditionally - see the toolbar note below. */}
                <Column
                    name="cycle"
                    calculateCellValue={cycleNumberOf}
                    groupIndex={groupedByCycle ? 0 : undefined}
                    sortOrder="desc"
                    visible={false}
                    groupCellRender={(cell: { value: number }) => (
                        <span className="step-cycle-group">{cycleGroupLabel(cell.value)}</span>
                    )}
                />
                <Grouping autoExpandAll={true} expandMode="rowClick" />

                <Paging enabled={false} />
                <Scrolling showScrollbar="always" />

                <Toolbar key={`toolbar-${phase?.id}-${stepCount}-${addButtonVisible ? 'show' : 'hide'}-${collapsed ? 'closed' : 'open'}`} visible={showToolbar}>
                    {/* Expander and name share one toolbar item. As two items the toolbar spaces them
                        itself and re-measures on resize, which opened a gap between them whenever
                        the task column's scrollbar came and went. One item makes the spacing ours.
                        Gated on showTaskName so only the toolbar acting as the task header gets an
                        expander - DC renders its own in the phase header row above.
                        The expander itself is the status icon (ticket 1034); TaskStatusIcon falls
                        back to a chevron for tasks that show no status. */}
                    <Item location="before" visible={showTaskName}>
                        {/* -5px cancelled the toolbar item's own padding so the chevron started
                            where the grid content does. With the chevron gone the status icon has
                            no arrow to its left, so it sits two pixels further in. */}
                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: '-3px' }}>
                            <TaskStatusIcon
                                phase={phase}
                                steps={currentCycleSteps}
                                onStatusIconClick={onClickShowRequestResponseSummary}
                                textOnly={hideStatusIcons()}
                                grantData={grantData}
                                collapsed={collapsed}
                                onToggleCollapsed={toggleCollapsed}
                                subtaskCount={stepCount}
                            />
                        </div>
                    </Item>

                    {/* Start Resubmission, in the overflow menu with Add and Delete. */}
                    <Item
                        location="after"
                        locateInMenu="always"
                        showText="inMenu"
                        widget="dxButton"
                        options={repeatButtonOptions}
                        visible={repeatInMenu}
                    />

                    <Item
                        location="after"
                        locateInMenu="always"
                        showText="inMenu"
                        widget="dxButton"
                        options={downloadAllOptions}
                        visible={downloadAllInMenu}
                    />

                    {addButtonVisible && (
                        <Item
                            location="after"
                            locateInMenu="always"
                            showText="inMenu"
                            widget="dxButton"
                            options={addButtonOptions}
                        />
                    )}

                    <Item
                        location="after"
                        locateInMenu="always"
                        showText="inMenu"
                        widget="dxButton"
                        options={deleteButtonOptions}
                        visible={deleteButtonVisible()}
                    />

                </Toolbar>

                <Selection mode="single" />
            </DataGrid>
        </div>
    );
}

export default React.memo(SubTaskListView, (prevProps, nextProps) => {
    return prevProps.phase?.id === nextProps.phase?.id &&
           prevProps.phase?.status === nextProps.phase?.status &&
           prevProps.hasLoaded === nextProps.hasLoaded &&
           prevProps.dataSource === nextProps.dataSource &&
           prevProps.mode === nextProps.mode &&
           prevProps.taskListClassName === nextProps.taskListClassName &&
           prevProps.addButtonVisible === nextProps.addButtonVisible &&
           prevProps.stepCount === nextProps.stepCount &&
           // Without this the collapse toggle does nothing: the comparator reports the props equal,
           // React skips the render, and the class that hides the rows is never applied.
           prevProps.collapsed === nextProps.collapsed &&
           prevProps.nameShownExternally === nextProps.nameShownExternally &&
           // A new cycle changes the phase without changing its id or status, and the group
           // headers and the start icon both read the cycles off it.
           prevProps.phase?.phaseSubmissions?.length === nextProps.phase?.phaseSubmissions?.length &&
           prevProps.groupedByCycle === nextProps.groupedByCycle &&
           prevProps.repeatButtonVisible === nextProps.repeatButtonVisible &&
           prevProps.repeatButtonOptions === nextProps.repeatButtonOptions &&
           prevProps.downloadAllVisible === nextProps.downloadAllVisible &&
           prevProps.downloadAllOptions === nextProps.downloadAllOptions;
});
